import { useRef, useEffect, useCallback, memo } from 'react'

// extraLayers: [{ markers: [{id, trajectoryPercent, visible}], color, visible }]
export const ArtemisSeekBar = memo(function ArtemisSeekBar({ progress, onSeek, onSeekEnd, missionStages = [], extraLayers = [], visible, paused = false, onTogglePause, onSkipBack, onSkipForward, guidedMode = true, onSetGuided, onSetAuto }) {
  const fillRef = useRef(null)
  const thumbRef = useRef(null)
  const trackRef = useRef(null)
  const isDraggingRef = useRef(false)
  const lastPctRef = useRef(0)

  // Imperatively update fill / thumb on progress change — avoids re-renders
  useEffect(() => {
    const pct = Math.max(0, Math.min(100, progress))
    if (fillRef.current) fillRef.current.style.width = `${pct}%`
    if (thumbRef.current) thumbRef.current.style.left = `${pct}%`
  }, [progress])

  const computeAndSeek = useCallback((e) => {
    if (!trackRef.current) return
    const rect = trackRef.current.getBoundingClientRect()
    const raw = (e.clientX - rect.left) / rect.width * 100
    const clamped = Math.max(0, Math.min(100, raw))
    lastPctRef.current = clamped

    // Imperatively update DOM immediately for visual feedback
    if (fillRef.current) fillRef.current.style.width = `${clamped}%`
    if (thumbRef.current) thumbRef.current.style.left = `${clamped}%`

    onSeek(clamped)
  }, [onSeek])

  const handlePointerDown = useCallback((e) => {
    isDraggingRef.current = true
    e.currentTarget.setPointerCapture(e.pointerId)
    computeAndSeek(e)
  }, [computeAndSeek])

  useEffect(() => {
    if (!visible) {
      isDraggingRef.current = false
      return
    }
    const handlePointerMove = (e) => {
      if (!isDraggingRef.current) return
      computeAndSeek(e)
    }
    const handlePointerUp = () => {
      // Fire once on scrub/tap release (not per pointermove) with the landing percent
      if (isDraggingRef.current) onSeekEnd?.(lastPctRef.current)
      isDraggingRef.current = false
    }
    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
    return () => {
      isDraggingRef.current = false
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
    }
  }, [computeAndSeek, visible, onSeekEnd])

  if (!visible) return null

  return (
    <div className="artemis-seek-bar">
      <div className="artemis-seek-bar__controls-row">
        <div className="artemis-seek-bar__controls">
          <button className="artemis-seek-bar__skip artemis-seek-bar__skip--back" onClick={onSkipBack} />
          <button className="artemis-seek-bar__playpause" data-state={paused ? 'paused' : 'playing'} onClick={onTogglePause} />
          <button className="artemis-seek-bar__skip artemis-seek-bar__skip--forward" onClick={onSkipForward} />
        </div>
      </div>
      <div
        ref={trackRef}
        className="artemis-seek-bar__track"
        onPointerDown={handlePointerDown}
      >
        <div ref={fillRef} className="artemis-seek-bar__fill" style={{ width: `${progress}%` }} />
        <div ref={thumbRef} className="artemis-seek-bar__thumb" style={{ left: `${progress}%` }} />
        {missionStages.filter(s => s.visible).map(stage => (
          <div
            key={stage.id}
            className={`artemis-seek-bar__stage-marker${progress >= stage.trajectoryPercent ? ' reached' : ''}`}
            style={{ left: `${stage.trajectoryPercent}%` }}
          />
        ))}
        {extraLayers.filter(l => l.visible).map(layer =>
          layer.markers.filter(m => m.visible).map(marker => (
            <div
              key={`${layer.id}_${marker.id}`}
              className="artemis-seek-bar__stage-marker artemis-seek-bar__stage-marker--layer"
              style={{
                left: `${marker.trajectoryPercent}%`,
                background: layer.color,
              }}
            />
          ))
        )}
      </div>
    </div>
  )
})
